import { Vue, Component, Ref, Prop, Emit } from 'vue-property-decorator'
import { uniqueId } from 'lodash'
import { Nullable } from '@/shared/types/app.types'
import { BModal } from 'bootstrap-vue'
import { ConfigurationPreformattedCreateRequest, ConfigurationPreformattedOntologyCreateRequest, ConfigurationPreformattedOntologyResponse, ConfigurationPreformattedPatchInBatchRequest, ConfigurationPreformattedResponse } from '@/modules/configurationModule/detail/local-samples/preformatted/services/preformatted.dto'
import { ConfigurationOntologyPreformattedTemplate, initialLocalPreformattedModalState, LocalPreformattedModalState } from '@/modules/configurationModule/detail/local-samples/preformatted/components/preformatted-modal/preformatted-modal.model'
import LocalPreformattedModalBasicForm from '@/modules/configurationModule/detail/local-samples/preformatted/components/preformatted-modal-basic-form/preformatted-modal-basic-form.vue'
import { preformattedConfigurationOntologyRelationshipServices } from '@/modules/configurationModule/detail/local-samples/preformatted/services/preformatted-ontology-relationship.service'
import SectionCodifications from '@/modules/diagnosisModule/views/form/sections/SectionCodifications.vue'
import { preformattedCodificationConfigurationServices } from '@/modules/configurationModule/detail/local-samples/preformatted/services/preformatted-codification.service'
import LocalPreformattedModalMacro from '@/modules/configurationModule/detail/local-samples/preformatted/components/preformatted-modal-macro/preformatted-modal-macro.vue'
import LocalPreformattedModalMicro from '@/modules/configurationModule/detail/local-samples/preformatted/components/preformatted-modal-micro/preformatted-modal-micro.vue'
import LocalPreformattedModalDiagnosis from '@/modules/configurationModule/detail/local-samples/preformatted/components/preformatted-modal-diagnosis/preformatted-modal-diagnosis.vue'
import LocalPreformattedModalAdditionalReport from '@/modules/configurationModule/detail/local-samples/preformatted/components/preformatted-modal-additional-report/preformatted-modal-additional-report.vue'
import i18n from '@/lang/i18n'
import { ConfigurationOntologiesState } from '@/modules/configurationModule/shared/components/configuration-ontologies/configuration-ontologies.model'
import ConfigurationOntologies from '@/modules/configurationModule/shared/components/configuration-ontologies/configuration-ontologies.vue'
import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { GridUtils } from '@/shared/util/grid-utils'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { PromiseUtils } from '@/shared/util/promise-utils'

@Component({
    components: {
        LocalPreformattedModalBasicForm,
        ConfigurationOntologies,
        SectionCodifications,
        LocalPreformattedModalMacro,
        LocalPreformattedModalMicro,
        LocalPreformattedModalDiagnosis,
        LocalPreformattedModalAdditionalReport
    }
})
export default class LocalPreformattedModal extends Vue {
    @Ref() readonly localPreformattedModal!: BModal
    @Prop(String) readonly actionTitle!: string
    @Prop(Number) readonly laboratoryId!: number
    @Prop() readonly broadcastChannelPostMessageFunction!: Function
    @Emit()
    update(localSampleTypePatch: ConfigurationPreformattedPatchInBatchRequest[]): ConfigurationPreformattedPatchInBatchRequest[] {
        return localSampleTypePatch
    }
    @Emit()
    create(localSampleTypeCreate: ConfigurationPreformattedCreateRequest): ConfigurationPreformattedCreateRequest {
        return localSampleTypeCreate
    }

    state: LocalPreformattedModalState
    constructor() {
        super()
        this.state = initialLocalPreformattedModalState()
    }

    get getPreformattedId(): number {
        return this.state.selectedRow?.id!
    }

    get codificationService(): typeof preformattedCodificationConfigurationServices {
        return preformattedCodificationConfigurationServices
    }

    get canAccessTab(): boolean {
        return !!this.state.selectedRow?.id
    }

    async show(selectedRow: Nullable<ConfigurationPreformattedResponse>): Promise<void> {
        this.state = initialLocalPreformattedModalState()
        await this.$validator.reset()
        if (selectedRow) {
            this._setSelectedRow(selectedRow)
            await this._getOntologies()
        }
        this.localPreformattedModal.show()
        this._forceRenderer()
    }

    hide(): void { this.localPreformattedModal.hide() }

    updateRow(selectedRow: Nullable<ConfigurationPreformattedResponse>): void {
        this._setSelectedRow(selectedRow)
    }

    spreadEvent(basicInfo: ConfigurationPreformattedPatchInBatchRequest | ConfigurationPreformattedCreateRequest): void {
        if (this.state?.selectedRow?.id) {
            this.update([basicInfo as ConfigurationPreformattedPatchInBatchRequest])
            return
        }
        this.create(basicInfo as ConfigurationPreformattedCreateRequest)
    }

    async createOntology(ontology: ConfigurationOntologiesState): Promise<void> {
        this.$_updateLoading(true)
        const createRelationshipOntology: ConfigurationPreformattedOntologyCreateRequest = {
            mostLikelyMorphologicCodeId: ontology?.mostLikelyMorphologicCode?.id,
            procedureId: ontology?.procedure?.id,
            sampleTypeId: ontology?.sampleType?.id!,
            topographicCodeId: ontology?.topographicCode?.id,
            preformattedId: this.state.selectedRow?.id!
        }
        const [error] = await this.$_toCall(preformattedConfigurationOntologyRelationshipServices.createPreformattedRelationshipOntologies(createRelationshipOntology, this.laboratoryId), true)
        this.$_updateLoading(false)
        if (error) {
            return
        }
        await this._getOntologies()
        if (this.broadcastChannelPostMessageFunction) {
            this.broadcastChannelPostMessageFunction()
        }
        this.$_showToastSuccess(i18n.t('configuration.toastMessages.created')?.toString())
    }

    async deleteOntology(ontologyId: number): Promise<void> {
        this.$_updateLoading(true)
        const [error] = await this.$_toCall(preformattedConfigurationOntologyRelationshipServices.deletePreformattedRelationshipOntologies(ontologyId, this.laboratoryId), true)
        this.$_updateLoading(false)
        if (error) {
            return
        }
        await this._getOntologies()
        if (this.broadcastChannelPostMessageFunction) {
            this.broadcastChannelPostMessageFunction()
        }
        this.$_showToastSuccess(i18n.tc('configuration.toastMessages.deleted', 1))
    }

    private _setSelectedRow(selectedRow: Nullable<ConfigurationPreformattedResponse>): void {
        this.state = {
            ...this.state,
            selectedRow
        }
    }

    private _forceRenderer(): void {
        this.state.modalUID = uniqueId()
    }

    private async _getOntologies(): Promise<void> {
        const filterModel: CommonFilterModel = {
            'preformatted.id': GridUtils.getNumberFilterModel('equals', this.state.selectedRow?.id)
        }
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const response = await PromiseUtils.toCallUnique(
            preformattedConfigurationOntologyRelationshipServices.getPreformattedRelationshipOntologies(filterModel, params, this.laboratoryId))
        if (response?.content) {
            this.state.ontologies = response.content.map((ontology: ConfigurationPreformattedOntologyResponse) => {
                const rowOntology: ConfigurationOntologyPreformattedTemplate = {
                    id: ontology.id,
                    sampleType: ontology.sampleType,
                    topographicCode: ontology.topographicCode,
                    mostLikelyMorphologicCode: ontology.mostLikelyMorphologicCode,
                    procedure: ontology.procedure,
                    preformattedId: ontology.id
                }
                return rowOntology
            })
        }
    }
}
